import { styled } from '@openeducation/oe-rn-themes';
import React, { useEffect, useState } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import { Menu, TextInput } from 'react-native-paper';

import { Loading } from '../../atoms';
import { SelectInputButton } from './select-input-button';

export interface SelectInputItem {
  title: string;
  value: string;
}

type PaperTextInputProps = React.ComponentProps<typeof TextInput>;

export type SelectInputProps = Partial<PaperTextInputProps> & {
  label: string;
  items: SelectInputItem[];
  defaultItemValue?: string;
  onChange: (value: SelectInputItem) => void;
  loading?: boolean;
  autoComplete?: string;
  icon?: React.ReactNode;
  menuStyle?: StyleProp<ViewStyle>;
};

export function SelectInput({
  items,
  label,
  defaultItemValue,
  onChange,
  loading = false,
  autoComplete = 'off',
  icon,
  menuStyle,
  ...props
}: SelectInputProps) {
  const [selectedItem, setSelectedItem] = useState(() =>
    items.find(({ value }) => value === defaultItemValue)
  );
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (defaultItemValue) {
      setSelectedItem(items.find(({ value }) => value === defaultItemValue));
    }
  }, [defaultItemValue, items]);

  function selectItem(item: SelectInputItem) {
    setSelectedItem(item);
    onChange?.(item);
    setVisible(false);
  }

  return (
    <TextInput
      autoComplete={autoComplete}
      mode="outlined"
      label={label}
      value={selectedItem ? selectedItem?.title : undefined}
      {...props}
      render={() =>
        loading ? (
          <StyledLoading />
        ) : (
          <Menu
            contentStyle={menuStyle}
            visible={visible}
            onDismiss={() => setVisible(false)}
            anchor={
              <SelectInputButton
                placeholder={label}
                title={selectedItem?.title}
                onPress={() => setVisible(true)}
                icon={icon}
              />
            }
          >
            {items?.map((item) => (
              <Menu.Item
                key={item.title}
                title={item.title}
                onPress={() => selectItem(item)}
              />
            ))}
          </Menu>
        )
      }
    />
  );
}

const StyledLoading = styled(Loading).attrs(({ theme }) => ({
  color: theme.color.primary,
}))`
  flex: 1;
  align-items: flex-end;
  justify-content: center;
  padding: 0 16px;
`;
